import { useRef } from 'react'

import type { Reference, StoreObject } from '@apollo/client'
import type { ModifierDetails } from '@apollo/client/cache'
import { CombinedGraphQLErrors } from '@apollo/client/errors'

import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'
import { debugLog } from '~publish/helpers/debugLog'
import { client } from '~publish/legacy/apollo-client'
import { PusherEvent, usePusherEvent } from '~publish/services/pusher'

import { ContentItemCard_ContentItem } from './fragments/ContentItemCard_ContentItem'

export const GetContentItemForRealTimeUpdate = graphql(
  `
    query GetContentItemForRealTimeUpdate($contentItemId: ContentItemId!) {
      contentItem(input: { id: $contentItemId }) {
        id
        # Keep in sync with each destination connection's node selection
        # (drafts list, calendar, Home drafts section, Board). This item is
        # written straight into their cache entries, so a missing field
        # breaks the read there.
        ...ContentItemCard_ContentItem
      }
    }
  `,
  [ContentItemCard_ContentItem],
)

export type GetContentItemForRealTimeUpdateData = ResultOf<
  typeof GetContentItemForRealTimeUpdate
>
export type RealTimeContentItem =
  GetContentItemForRealTimeUpdateData['contentItem']

/**
 * Pusher only sends the id, so we refetch the item by id to get its data.
 */
type PusherContentItemEventData = {
  organizationId: string
  contentItemId: string
}

function queryContentItem(
  contentItemId: string,
): Promise<{ data: GetContentItemForRealTimeUpdateData | undefined }> {
  return client.query({
    query: GetContentItemForRealTimeUpdate,
    variables: { contentItemId },
    fetchPolicy: 'no-cache',
    // `fetchContentItemDeduped` shares a fetch within one dispatch. Apollo's
    // own dedupe would also join a later event to an older, stale request.
    context: { queryDeduplication: false },
  })
}

type ContentItemFetch = {
  request: ReturnType<typeof queryContentItem>
  eventType: ContentItemUpsertEventType
}

// One Pusher event reaches every mounted copy of this hook in the same
// synchronous dispatch, so they share one fetch. A later event for the same
// id gets its own fetch, because a shared in-flight one may predate its edit.
const inFlightContentItemFetches = new Map<string, ContentItemFetch>()

// The newest fetch for each id. Responses can arrive out of order, so only
// this fetch may write to the cache.
const latestContentItemFetches = new Map<string, ContentItemFetch>()

function fetchContentItemDeduped(
  contentItemId: string,
  eventType: ContentItemUpsertEventType,
): ContentItemFetch {
  const existing = inFlightContentItemFetches.get(contentItemId)
  if (existing) {
    return existing
  }

  const superseded = latestContentItemFetches.get(contentItemId)
  const itemFetch: ContentItemFetch = {
    request: queryContentItem(contentItemId),
    // A fetch that replaces a pending creation still reports the creation,
    // or consumers would never insert the item.
    eventType: superseded?.eventType === 'created' ? 'created' : eventType,
  }
  inFlightContentItemFetches.set(contentItemId, itemFetch)
  latestContentItemFetches.set(contentItemId, itemFetch)
  queueMicrotask(() => {
    inFlightContentItemFetches.delete(contentItemId)
  })

  // A macrotask runs after every consumer's continuation has checked it.
  const forget = (): void => {
    setTimeout(() => {
      if (latestContentItemFetches.get(contentItemId) === itemFetch) {
        latestContentItemFetches.delete(contentItemId)
      }
    }, 0)
  }
  itemFetch.request.then(forget).catch(forget)
  return itemFetch
}

// GraphQL extension code the backend attaches to a NotFoundError (see
// graphql/src/_utils/errors.ts). Matching on the code rather than the message
// keeps this resilient to copy changes.
const NOT_FOUND_ERROR_CODE = 'NOT_FOUND'

/**
 * True when the refetch failed specifically because the content item no
 * longer exists. Other errors (forbidden, transient server failures) must not
 * be treated as a deletion.
 */
export const isContentItemNotFoundError = (error: unknown): boolean =>
  CombinedGraphQLErrors.is(error) &&
  error.errors.some(
    (graphQLError) => graphQLError.extensions?.code === NOT_FOUND_ERROR_CODE,
  )

// How long a deleted content item id stays in the guard. Long enough to cover
// Pusher delivery jitter between a delete event and any racing update event,
// short enough that the guard never grows unbounded on the org-wide stream.
export const DELETED_CONTENT_ITEM_GUARD_TTL_MS = 60_000

/**
 * Records a content item id as recently deleted (with an expiry), pruning
 * entries whose window has passed.
 */
export const rememberDeletedContentItem = (
  deletedItems: Map<string, number>,
  id: string,
): void => {
  const now = Date.now()
  for (const [itemId, expiresAt] of deletedItems) {
    if (expiresAt <= now) {
      deletedItems.delete(itemId)
    }
  }
  deletedItems.set(id, now + DELETED_CONTENT_ITEM_GUARD_TTL_MS)
}

/**
 * Returns true if the content item was deleted within the guard window.
 * Expired entries are dropped on access so the map self-heals without timers.
 */
export const wasContentItemRecentlyDeleted = (
  deletedItems: Map<string, number>,
  id: string,
): boolean => {
  const expiresAt = deletedItems.get(id)
  if (expiresAt === undefined) {
    return false
  }
  if (expiresAt <= Date.now()) {
    deletedItems.delete(id)
    return false
  }
  return true
}

type ContentItemCacheEdge = { node: Reference | StoreObject }

/**
 * Drops the item's edge from one cached connection, lowering `totalCount`
 * when the connection held it.
 */
function removeEdgeFromConnection(
  deletedId: string,
): (
  existingConnection: Reference | StoreObject,
  details: ModifierDetails,
) => Reference | StoreObject {
  return (existingConnection, { readField }) => {
    if (!existingConnection || '__ref' in existingConnection) {
      return existingConnection
    }

    const edges =
      readField<ContentItemCacheEdge[]>('edges', existingConnection) ?? []
    const remainingEdges = edges.filter(
      (edge) => readField('id', edge.node) !== deletedId,
    )
    if (remainingEdges.length === edges.length) {
      return existingConnection
    }

    const totalCount = readField<number>('totalCount', existingConnection)
    return {
      ...existingConnection,
      edges: remainingEdges,
      ...(typeof totalCount === 'number'
        ? { totalCount: Math.max(0, totalCount - 1) }
        : {}),
    }
  }
}

/**
 * Removes a content item from every cached connection, then evicts it.
 */
export function evictContentItem(id: string): void {
  const normalizedId = client.cache.identify({
    id,
    __typename: 'ContentItem',
  })
  if (!normalizedId) {
    return
  }

  // Apollo drops a dangling reference only when it sits straight in a list.
  // Here it sits in `edge.node`, which would leave every connection that
  // still holds the edge incomplete and force a network read.
  client.cache.modify({
    fields: {
      contentItems: removeEdgeFromConnection(id),
      drafts: removeEdgeFromConnection(id),
    },
  })
  client.cache.evict({ id: normalizedId })
}

export type ContentItemUpsertEventType = 'created' | 'updated'

type UseContentItemRealTimePusherUpdatesOptions = {
  /** Ignores every event, for a consumer whose query is skipped. */
  skip?: boolean
  /**
   * Called with the refetched item after a created or updated event, and
   * which of the two it was.
   */
  onItemFetched: (
    item: RealTimeContentItem,
    eventType: ContentItemUpsertEventType,
  ) => void
  /**
   * Called with the id after a deleted event, or after an updated event that
   * raced a deletion (the refetch comes back NOT_FOUND).
   */
  onDeleted: (id: string) => void
}

/**
 * Resolves the content item Pusher events into a fresh item (created or
 * updated) or a deletion (deleted, or an update that raced a delete).
 *
 * A content item has no channel, so the org-wide Pusher subscription is the
 * only access gate. A per-channel check, as posts use, would drop every event.
 */
export function useContentItemRealTimePusherUpdates({
  skip = false,
  onItemFetched,
  onDeleted,
}: UseContentItemRealTimePusherUpdatesOptions): void {
  // Ids this client saw deleted (id -> expiry), so a racing update skips a
  // refetch that can only fail or resurrect the item.
  const deletedItems = useRef<Map<string, number>>(new Map())

  const handleDeleted = (contentItemId: string): void => {
    rememberDeletedContentItem(deletedItems.current, contentItemId)
    // Each consumer reads its cached page to adjust its own `totalCount`, so
    // it runs before the eviction. The microtask lets every other mounted
    // copy of this hook, bound later in the same dispatch, do the same.
    onDeleted(contentItemId)
    queueMicrotask(() => evictContentItem(contentItemId))
  }

  usePusherEvent(
    PusherEvent.CONTENT_ITEM_DELETED,
    (data: PusherContentItemEventData): void => {
      if (skip) return
      debugLog('💽 CONTENT_ITEM_DELETED', data)
      handleDeleted(data.contentItemId)
    },
  )

  const handleUpsert = async (
    eventType: ContentItemUpsertEventType,
    data: PusherContentItemEventData,
  ): Promise<void> => {
    if (skip) return
    debugLog(`💽 CONTENT_ITEM_${eventType.toUpperCase()}`, data)
    const { contentItemId } = data

    if (wasContentItemRecentlyDeleted(deletedItems.current, contentItemId)) {
      return
    }

    const itemFetch = fetchContentItemDeduped(contentItemId, eventType)
    const outcome = await itemFetch.request.then(
      (result) => ({ item: result.data?.contentItem, error: undefined }),
      (error: unknown) => ({ item: undefined, error }),
    )

    // A newer fetch for this id started while this one was in flight.
    if (latestContentItemFetches.get(contentItemId) !== itemFetch) {
      return
    }

    // A delete that landed during the fetch wins: the fetch may have read the
    // row just before the delete committed, and nothing revisits the id later.
    if (wasContentItemRecentlyDeleted(deletedItems.current, contentItemId)) {
      return
    }

    if (!outcome.item) {
      // NOT_FOUND: this event raced ahead of the item's delete event.
      if (isContentItemNotFoundError(outcome.error)) {
        handleDeleted(contentItemId)
        return
      }
      // Nothing awaits this handler, so log instead of throwing. The next
      // event or refetch brings the item up to date.
      if (outcome.error) {
        debugLog('💽 content item refetch failed', outcome.error)
      }
      return
    }

    try {
      onItemFetched(outcome.item, itemFetch.eventType)
    } catch (error) {
      debugLog('💽 content item cache write failed', error)
    }
  }

  usePusherEvent(
    PusherEvent.CONTENT_ITEM_CREATED,
    (data: PusherContentItemEventData): Promise<void> =>
      handleUpsert('created', data),
  )
  usePusherEvent(
    PusherEvent.CONTENT_ITEM_UPDATED,
    (data: PusherContentItemEventData): Promise<void> =>
      handleUpsert('updated', data),
  )
}
